/**
 * =============================================================================
 * FOOTER - Rodapé do Site
 * =============================================================================
 *
 * Rodapé com 4 colunas: marca, links rápidos, serviços e contacto.
 * Inclui botão de definições de cookies e barra de copyright.
 */

import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { ROUTES } from "@/constants/routes";
import { CONTACT } from "@/constants/site";
import { CookieSettingsButton } from "@/components/cookies/CookieSettingsButton";

type QuickLinkItem = { to: string; label: string } | { label: string; children: { to: string; label: string }[] };

const quickLinks: QuickLinkItem[] = [
  { to: ROUTES.home, label: "Início" },
  {
    label: "Livros",
    children: [
      { to: ROUTES.livroManual, label: "Manual para Professores" },
      { to: ROUTES.livroSerMaior, label: "Ser Maior" },
    ],
  },
  { to: ROUTES.proBono, label: "Pro Bono" },
];

const serviceLinks = [
  { to: ROUTES.hipnoterapia, label: "Hipnoterapia" },
  { to: ROUTES.superAprendizagem, label: "Super aprendizagem" },
  { to: ROUTES.formacao, label: "Formação" },
  { to: ROUTES.psicologia, label: "Psicologia" },
];

const contactInfo = [
  {
    icon: Phone,
    label: "Telefone",
    value: CONTACT.phoneDisplay,
    href: `tel:${CONTACT.phone}`,
  },
  {
    icon: Mail,
    label: "Email",
    value: CONTACT.email,
    href: `mailto:${CONTACT.email}`,
  },
  {
    icon: MapPin,
    label: "Morada",
    value: CONTACT.address.display,
    href: "https://maps.google.com/?q=Travessa+do+Ferro+7+Viseu",
    isExternal: true,
  },
];

export default function Footer() {
  return (
    <footer className="bg-gradient-to-b from-deepOcean to-[#131f19] text-pureWhite/90">
      <div className="container-main py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          <div>
            <h3 className="font-playfair text-xl font-bold text-pureWhite mb-1">
              PEDRO <span className="font-normal italic">de</span> FREITAS
            </h3>
            <p className="text-xs tracking-[0.3em] text-softSky uppercase font-medium mb-4">
              Oficina da Mente
            </p>
            <p className="text-sm text-pureWhite/70 leading-relaxed">
              Hipnoterapia, formação e desenvolvimento pessoal.
            </p>
            <p className="text-sm text-pureWhite/70 leading-relaxed mt-4">
              Ensinamos a usar ferramentas para uma vida.
            </p>
            <img
              src="/images/logo-oficina-da-mente.png"
              alt="Logótipo Oficina da Mente"
              className="h-20 w-20 object-contain rounded-full bg-pureWhite p-1.5 mt-6 shadow-soft"
              width={80}
              height={80}
            />
          </div>

          <div>
            <h4 className="font-playfair text-base font-semibold text-pureWhite mb-4">
              Links rápidos
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  {"to" in link ? (
                    <Link
                      to={link.to}
                      className="text-sm text-pureWhite/70 hover:text-softSky transition-colors duration-200"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <div>
                      <span className="text-sm text-pureWhite/90 font-medium">
                        {link.label}
                      </span>
                      <ul className="mt-1 ml-3 space-y-1 border-l border-pureWhite/20 pl-3">
                        {link.children.map((child) => (
                          <li key={child.to}>
                            <Link
                              to={child.to}
                              className="text-sm text-pureWhite/60 hover:text-softSky transition-colors duration-200"
                            >
                              {child.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-playfair text-base font-semibold text-pureWhite mb-4">
              Serviços
            </h4>
            <ul className="space-y-2">
              {serviceLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-pureWhite/70 hover:text-softSky transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-playfair text-base font-semibold text-pureWhite mb-4">
              Contacto
            </h4>
            <ul className="space-y-3">
              {contactInfo.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="flex items-start gap-2 text-sm text-pureWhite/70 hover:text-softSky transition-colors duration-200"
                    target={item.isExternal ? "_blank" : undefined}
                    rel={item.isExternal ? "noopener noreferrer" : undefined}
                  >
                    <item.icon size={16} className="mt-0.5 text-softSky flex-shrink-0" aria-hidden="true" />
                    <span>{item.value}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-pureWhite/10">
        <div className="container-main py-5">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-xs text-pureWhite/50">
            <span>
              {new Date().getFullYear()} © Pedro de Freitas. Todos os direitos reservados por{" "}
              <a
                href="https://digitalwebviseu.pt"
                target="_blank"
                rel="noopener noreferrer"
                className="text-pureWhite/70 hover:text-softSky transition-colors duration-200"
              >
                digitalwebviseu.pt
              </a>
            </span>
            <span className="hidden sm:inline text-pureWhite/30">|</span>
            <Link
              to={ROUTES.privacidade}
              target="_blank"
              rel="noopener noreferrer"
              className="text-pureWhite/70 hover:text-softSky transition-colors duration-200"
            >
              Política de privacidade
            </Link>
            <span className="hidden sm:inline text-pureWhite/30">|</span>
            <Link
              to={ROUTES.termos}
              target="_blank"
              rel="noopener noreferrer"
              className="text-pureWhite/70 hover:text-softSky transition-colors duration-200"
            >
              Termos e condições
            </Link>
            <span className="hidden sm:inline text-pureWhite/30">|</span>
            <CookieSettingsButton className="text-pureWhite/70 hover:text-softSky" />
          </div>
        </div>
      </div>
    </footer>
  );
}
